import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { Anchor, ArrowDown, Activity, Shield, Crosshair, MapPin, Gauge, Database } from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import VesselConfigurator from './VesselConfigurator';
import TechnicalDifferentiators from './TechnicalDifferentiators';
import AnatomyDetailed from './AnatomyDetailed';
import LabProof from './LabProof';
import Specs from './Specs';
import WhyPricingAndDelivery from './WhyPricingAndDelivery';

export default function EndurancePage() {
const { content, resolveMediaUrl, assets, mission } = useBrand();

return (
<div className="bg-[#E4E3E0] min-h-screen">
  <Helmet>
    <title>OWS EXPEDITION CX2 | Tactical Expedition Vessel | OWS</title>
    <meta name="description"
      content="The OWS EXPEDITION CX2: Built for multi-day open ocean exploration. Featuring 1600 liters of displacement and secondary air-cell redundancy." />
    <meta name="keywords"
      content="OWS EXPEDITION CX2, expedition SUP, offshore paddleboarding, maritime overlanding, ocean survival vessel" />
  </Helmet>

  {/* MISSION BRIEFING HERO */}
  <section className="relative h-screen flex items-center bg-[#18181A] overflow-hidden">
    {/* Cinematic Background */}
    <div className="absolute inset-0">
      {assets.vesselVideo ? (
      <video autoPlay loop muted playsInline src={resolveMediaUrl(assets.vesselVideo) || undefined}
        className="w-full h-full object-cover opacity-50 object-center" />
      ) : (
      <img src={resolveMediaUrl(assets.boardImage) || undefined} alt="Vessel Displacement View"
        className="w-full h-full object-cover opacity-40 object-center" referrerPolicy="no-referrer" />
      )}

      {/* Technical Overlays */}
      <div className="absolute inset-0 pointer-events-none z-10">
        <div className="absolute top-12 left-12 p-8 border-l border-t border-white/20">
          <div className="flex items-center gap-3 text-ows-teal mb-4">
            <Activity className="h-4 w-4 animate-pulse" />
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.5em]">SYSTEM: ONLINE</span>
          </div>
          <div className="space-y-1">
            <div className="text-[8px] font-mono text-white/40 uppercase tracking-widest">B01-DEPLOYMENT: READY</div>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#18181A] via-[#18181A]/10 to-transparent" />
    </div>

    {/* Hero Content */}
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-20 pt-20">
      <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.2,
        ease: "circOut" }} className="max-w-4xl">
        <div className="flex items-center gap-4 text-ows-teal mb-8">
          <Crosshair className="h-6 w-6" />
          <span
            className="text-sm font-mono font-bold tracking-[0.5em] uppercase border-b border-ows-teal pb-1">EXPEDITION:
            BEYOND THE SHORELINE. BEYOND THE LIMIT.</span>
        </div>

        <h1
          className="text-6xl md:text-8xl lg:text-9xl font-display font-light uppercase tracking-tighter text-white mb-6 leading-[0.85]">
          NOT A <br />
          <span className="text-ows-teal italic font-serif">PADDLE BOARD.</span>
        </h1>

        <p className="text-xl md:text-2xl text-white/80 font-light mb-12 tracking-tight">
          It is a <span className="text-white font-medium uppercase tracking-widest">Vessel.</span>
        </p>

        <div className="flex flex-col md:flex-row gap-12 items-start md:items-center">
          <div className="flex-1 border-l-2 border-ows-teal pl-8">
            <p className="text-[10px] font-mono font-bold text-white/60 uppercase tracking-[0.4em] mb-2">The Philosophy
            </p>
            <p
              className="text-xs md:text-sm text-white font-mono uppercase tracking-[0.2em] leading-relaxed max-w-md italic">
              "That feeling of working twice as hard to go half as far is not you. That is the board."
            </p>
          </div>

          <div className="flex-1 flex flex-wrap gap-4 pt-4 md:pt-0">
            <a href="#configurator" onClick={(e)=> { e.preventDefault();
              document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center gap-4 px-8 py-5 bg-white text-[#141414] text-[9px] font-mono font-bold
              tracking-[0.4em] uppercase hover:bg-ows-teal hover:text-white transition-all rounded-xl group shadow-2xl"
              >
              PRE-ORDER CX2
              <ArrowDown className="h-3 w-3 group-hover:translate-y-1 transition-transform" />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  </section>

  {/* WHAT WE ARE */}
  <section id="what-we-are" className="bg-[#E4E3E0] py-32 relative text-[#141414]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4">
          <h2 className="text-xs font-mono font-bold text-ows-teal uppercase tracking-[0.8em] mb-6">The Diagnosis</h2>
          <p className="text-2xl md:text-3xl font-display font-light uppercase leading-tight tracking-tighter">
            That feeling is not you. <br />
            <span className="text-ows-teal italic font-serif text-4xl md:text-5xl">That is the board.</span>
          </p>
        </div>
        <div className="lg:col-span-8 space-y-8">
          <p className="text-lg md:text-2xl text-[#141414]/80 font-light leading-relaxed">
            You bought your first inflatable paddle board online. It was fine. You learned to stand, learned to paddle,
            started going further.
          </p>
          <p className="text-lg md:text-2xl text-[#141414]/80 font-light leading-relaxed">
            Then one day the conditions changed — wind came up, chop hit from the side, you were further from shore than
            you planned — and you felt it. The board flexed under you. You had no leverage. You worked twice as hard to
            go half as far.
          </p>
          <div
            className="pt-8 border-t border-[#141414]/10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-sm font-mono font-bold uppercase tracking-widest text-[#141414]/40">
              Open Water Syndicate is a Toronto-based expedition crew. We do not make lifestyle products.
            </p>
            <a href="#configurator" onClick={(e)=> { e.preventDefault();
              document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#141414] text-white text-[10px] font-mono
              font-bold tracking-[0.3em] uppercase hover:bg-ows-teal transition-all rounded-xl shrink-0 shadow-lg group"
              >
              PRE-ORDER CX2
              <ArrowDown className="h-3.5 w-3.5 group-hover:translate-y-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>

  {/* WHAT WE ARE - EXPEDITION BACKGROUND */}
  <section id="built-by-expedition" className="bg-[#EDEDEA] py-24 relative text-[#141414] border-t border-[#141414]/10">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* 1. Eyebrow (teal, tracked) */}
      <div className="text-xs md:text-sm font-mono font-bold text-ows-teal uppercase tracking-[0.6em] mb-6">
        BUILT BY EXPEDITION
      </div>

      {/* 2. Opening (body, mixed weight) */}
      <p className="text-xl md:text-2xl text-[#141414] leading-relaxed font-sans mb-6">
        <span className="font-bold">I'm Taras.</span> Marine engineer. Endurance paddler.
      </p>

      {/* 3. Stats block (bold, large) */}
      <div className="text-2xl md:text-3xl font-bold text-[#141414] leading-snug space-y-3 my-8 font-sans">
        <p>52.87 km solo across Lake Ontario. Standing. No support.</p>
        <p>110 km circumnavigation of Isla Espíritu Santo and Isla Partida.</p>
        <p>Three previous Lake Ontario crossings.</p>
      </div>

      {/* 4. Separator line */}
      <hr className="my-8 border-t border-[#141414]/15" />

      {/* 5. Narrative body */}
      <p className="text-lg md:text-xl text-[#141414]/85 leading-relaxed font-sans font-normal my-8">
        We carried thirty litres of water per person before touching food or safety gear. We paddled through wildfire
        smoke so thick there was no horizon. We paddled fourteen hours across the lake. For the final 10 km — into a 20
        km/h headwind, in the dark, completely depleted of energy.
      </p>

      {/* 6. Separator line */}
      <hr className="my-8 border-t border-[#141414]/15" />

      {/* 7. Pull quote (italic, teal, small caps) */}
      <blockquote
        className="text-ows-teal italic font-mono text-base md:text-lg font-bold tracking-wider leading-relaxed my-8 uppercase [font-variant:small-caps]">
        EVERY BOARD WE TOOK ON THOSE CROSSINGS WAS SOMEONE ELSE'S BEST PRODUCT. WE FOUND OUT EXACTLY WHERE EACH ONE
        FAILED. THEN WE BUILT THE ONE WE NEEDED.
      </blockquote>

      {/* 8. Separator line */}
      <hr className="my-8 border-t border-[#141414]/15" />

      {/* 9. Closing statement (massive, bold, black) */}
      <h2
        className="text-4xl md:text-6xl lg:text-7xl font-display font-black text-[#141414] uppercase tracking-tighter my-8 leading-none">
        THIS ONE DOESN'T.
      </h2>

      {/* 10. Brand footer & CTA */}
      <div
        className="pt-8 border-t border-[#141414]/15 flex flex-col sm:flex-row items-center justify-between gap-6 mt-8">
        <p
          className="text-xs md:text-sm font-mono font-bold uppercase tracking-[0.2em] text-[#141414]/50 [font-variant:small-caps]">
          OPEN WATER SYNDICATE IS A TORONTO-BASED EXPEDITION CREW. WE DO NOT MAKE LIFESTYLE PRODUCTS.
        </p>
        {/* <a href="#configurator" onClick={(e)=> { e.preventDefault();
          document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="inline-flex items-center gap-3 px-8 py-4 bg-ows-teal text-white text-[10px] font-mono font-bold
          tracking-[0.3em] uppercase hover:bg-[#141414] transition-all rounded-xl shrink-0 shadow-xl group"
          >
          PRE-ORDER CX2
          <ArrowDown className="h-3.5 w-3.5 group-hover:translate-y-1 transition-transform" />
        </a> */}
      </div>
    </div>
  </section>

  {/* WHAT GOES WRONG OUT THERE */}
  <section className="bg-[#18181A] py-32 text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-24">
        <h2 className="text-[10px] font-mono font-bold text-ows-teal uppercase tracking-[0.8em] mb-4">The Reality</h2>
        <h3 className="text-4xl md:text-6xl font-display font-light uppercase tracking-tighter">What Goes <span
            className="text-ows-teal">Wrong</span> Out There</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="p-10 border border-white/10 rounded-[3rem] hover:bg-white/5 transition-all">
          <Shield className="h-8 w-8 text-ows-orange mb-6" />
          <h4 className="text-lg font-bold uppercase tracking-widest mb-4">You Cannot Brace</h4>
          <p className="text-xs font-mono text-white/50 leading-relaxed uppercase tracking-tighter">
            Raised side chambers force your feet toward the center. When side chop hits, you have no leverage. The board
            trips you. The OWS EXPEDITION CX2 has a wide flat 32 inch deck, rail to rail. You brace against the wave.
          </p>
        </div>
        <div className="p-10 border border-white/10 rounded-[3rem] hover:bg-white/5 transition-all">
          <Crosshair className="h-8 w-8 text-ows-teal mb-6" />
          <h4 className="text-lg font-bold uppercase tracking-widest mb-4">The Board Fights You</h4>
          <p className="text-xs font-mono text-white/50 leading-relaxed uppercase tracking-tighter">
            Flat bottom hulls plow water. On a 54km crossing into a headwind, the drag compounds hour by hour. The OWS
            EXPEDITION CX2 has a V-shaped nose hull that cuts the surface instead of pushing it.
          </p>
        </div>
        <div className="p-10 border border-white/10 rounded-[3rem] hover:bg-white/5 transition-all">
          <Activity className="h-8 w-8 text-ows-orange mb-6" />
          <h4 className="text-lg font-bold uppercase tracking-widest mb-4">Bends Under Load</h4>
          <p className="text-xs font-mono text-white/50 leading-relaxed uppercase tracking-tighter">
            Single chamber boards "taco-flex" under expedition loads. You are paddling a hammock, not a board. The OWS
            EXPEDITION CX2 dual chamber spine eliminates flex. It paddles like a hard board.
          </p>
        </div>
        <div className="p-10 border border-white/10 rounded-[3rem] hover:bg-white/5 transition-all">
          <Shield className="h-8 w-8 text-ows-teal mb-6" />
          <h4 className="text-lg font-bold uppercase tracking-widest mb-4">Point of Failure</h4>
          <p className="text-xs font-mono text-white/50 leading-relaxed uppercase tracking-tighter">
            A seam failure offshore on a single chamber board is a rescue situation. The OWS EXPEDITION CX2 has two
            fully independent air chambers. If the outer is compromised, the inner gets you home.
          </p>
        </div>
      </div>
      <div className="mt-16 text-center">
        <a href="#configurator" onClick={(e)=> { e.preventDefault();
          document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="inline-flex items-center gap-3 px-10 py-5 bg-ows-teal text-white text-[10px] font-mono font-bold
          tracking-[0.3em] uppercase hover:bg-white hover:text-[#141414] transition-all rounded-full shadow-2xl group"
          >
          PRE-ORDER CX2
          <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform" />
        </a>
      </div>
    </div>
  </section>

  {/* TECHNICAL DOSSIER */}
  <section className="bg-[#18181A] py-32 text-white border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-12">
          <div
            className="p-12 md:p-20 bg-white/5 border border-white/10 rounded-[4rem] backdrop-blur-xl relative overflow-hidden">
            {/* Background Detail */}
            <div className="absolute top-0 right-0 p-12 opacity-10">
              <Database className="h-64 w-64 text-ows-teal rotate-12" />
            </div>

            <div className="relative z-10 max-w-4xl">
              <div className="flex items-center gap-4 mb-8">
                <Shield className="h-6 w-6 text-ows-teal" />
                <h2 className="text-xs font-mono font-bold text-ows-teal uppercase tracking-[0.6em]">Vessel Dossier: OWS
                  EXPEDITION CX2</h2>
              </div>

              <p
                className="text-3xl md:text-5xl font-display font-light uppercase tracking-tighter leading-tight mb-12">
                Engineering <span className="text-ows-teal">Redundancy</span> <br />
                Beyond the shoreline.
              </p>

              <div className="space-y-6">
                <p className="text-xl md:text-2xl text-white/95 font-sans font-normal leading-relaxed">
                  The OWS EXPEDITION CX2 is a 32" wide, long-range expedition vessel engineered for industrial-grade
                  survival. Built with elite woven drop-stitch and heat-fused rails, it features a displacement hull for
                  superior tracking and a Double Chamber for mission-critical safety.
                </p>
                <p className="text-lg md:text-xl text-white/75 font-sans font-normal leading-relaxed">
                  The desert-ready light tones prevent overheating, while the modular system—featuring a 9" centre fin
                  and dual shallow-water fins—ensures tactical versatility. Stripping away marketing fluff, the OWS
                  EXPEDITION CX2 is your mechanical insurance when the shore disappears.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-12 border-t border-white/10">
                <div>
                  <span
                    className="block text-[8px] font-mono font-bold text-ows-teal uppercase tracking-widest mb-1">Architecture</span>
                  <span className="block text-[10px] font-mono text-white/50 uppercase">Woven Drop-Stitch</span>
                </div>
                <div>
                  <span
                    className="block text-[8px] font-mono font-bold text-ows-teal uppercase tracking-widest mb-1">Joint
                    Integrity</span>
                  <span className="block text-[10px] font-mono text-white/50 uppercase">Heat-Fused Rails</span>
                </div>
                <div>
                  <span
                    className="block text-[8px] font-mono font-bold text-ows-teal uppercase tracking-widest mb-1">Safety
                    Class</span>
                  <span className="block text-[10px] font-mono text-white/50 uppercase">Double Chamber</span>
                </div>
                <div>
                  <span
                    className="block text-[8px] font-mono font-bold text-ows-teal uppercase tracking-widest mb-1">Mission
                    Profile</span>
                  <span className="block text-[10px] font-mono text-white/50 uppercase">Reconnaissance</span>
                </div>
              </div>

              <div
                className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <span className="block text-[10px] font-mono font-bold text-ows-teal uppercase tracking-widest">BATCH
                    01 PRODUCTION SLOT</span>
                  <span className="block text-[11px] font-mono text-white/60 uppercase">200 Units. First-come,
                    first-built.</span>
                </div>
                <a href="#configurator" onClick={(e)=> { e.preventDefault();
                  document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-ows-teal text-white text-[10px] font-mono
                  font-bold tracking-[0.3em] uppercase hover:bg-white hover:text-[#141414] transition-all rounded-xl
                  shrink-0 shadow-xl group"
                  >
                  PRE-ORDER CX2
                  <ArrowDown className="h-3.5 w-3.5 group-hover:translate-y-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  {/* WHY THE OWS EXPEDITION CX2 WORKS */}
  <TechnicalDifferentiators />

  {/* THE MANIFESTO: BORN AT THE BREAKING POINT */}
  <section className="bg-[#E4E3E0] py-32 relative overflow-hidden text-[#141414]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="space-y-12">
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-8 bg-ows-teal" />
            <h2 className="text-xs font-mono font-bold text-ows-teal uppercase tracking-[0.8em]">Safety Protocol</h2>
            <div className="h-px w-8 bg-ows-teal" />
          </div>
          <h3
            className="text-4xl md:text-6xl lg:text-7xl font-display font-light text-[#141414] uppercase tracking-tighter leading-[1.05]">
            Navigation <br />
            <span className="italic font-serif">White.</span>
          </h3>
        </div>

        <div className="relative">
          <p
            className="text-xl md:text-2xl text-[#141414]/90 font-light leading-relaxed tracking-tight max-w-2xl mx-auto">
            Dark decks reflect heat, internal pressure spikes, and adhesives soften. Dark boards are also invisible at
            night. <span className="text-ows-teal font-bold uppercase tracking-widest lg:block mt-4">Invisible is
              dangerous.</span>
          </p>
          <p
            className="text-lg text-[#141414]/60 mt-8 leading-relaxed max-w-2xl mx-auto font-mono uppercase tracking-tighter text-sm">
            The OWS EXPEDITION CX2 is white. White reflects heat and keeps internal pressure stable across a full day.
            White is the most visible color on open water after dark. These are safety decisions, not aesthetic ones.
          </p>
        </div>

        <div className="pt-6">
          <a href="#configurator" onClick={(e)=> { e.preventDefault();
            document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-3 px-10 py-5 bg-ows-teal text-white text-[10px] font-mono font-bold
            tracking-[0.3em] uppercase hover:bg-[#141414] transition-all rounded-full shadow-2xl group"
            >
            PRE-ORDER CX2
            <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform" />
          </a>
        </div>
      </motion.div>
    </div>
  </section>

  {/* ENGINEERING PROOF */}
  <AnatomyDetailed />

  {/* BENCHMARK DATA */}
  <Specs />

  {/* WHY $999 CAD & DELIVERY */}
  <WhyPricingAndDelivery />

  {/* PRE-CONFIGURATOR ACTION STRIP */}
  <section className="bg-[#EDEDEA] py-12 border-t border-b border-[#141414]/10 text-center">
    <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-6">
      <div className="text-left">
        <p className="text-xs font-mono font-bold uppercase tracking-widest text-ows-teal">BATCH 01 PRODUCTION
          ALLOCATION</p>
        <p className="text-sm font-sans font-bold text-[#141414] uppercase tracking-tight">200 UNITS. FIRST-COME,
          FIRST-BUILT.</p>
      </div>
      <a href="#configurator" onClick={(e)=> { e.preventDefault();
        document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
        className="inline-flex items-center gap-3 px-10 py-5 bg-ows-teal text-white text-[10px] font-mono font-bold
        tracking-[0.3em] uppercase hover:bg-[#141414] transition-all rounded-full shadow-2xl shrink-0 group"
        >
        LOCK YOUR BATCH 01 UNIT
        <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform" />
      </a>
    </div>
  </section>

  {/* THE INNER CIRCLE (PURCHASE) */}
  <VesselConfigurator />
</div>
);
}